import { useState, useEffect } from "react";
import {
    Box,
    Button,
    TextField,
    useMediaQuery,
    Typography,
    useTheme,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Container,
    Grid
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { Formik } from "formik";
import * as yup from "yup";
import { useNavigate } from "react-router-dom";
import Dropzone from "react-dropzone";
import validator from "validator";
import { Snackbar, Alert } from "@mui/material";
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import logo from './img/logo.png';

import Cropper from "react-easy-crop";
import Slider from "@mui/material/Slider";
import Dialog from "@mui/material/Dialog";
import getCroppedImg from "./utils/cropImage"; // You'll create this function
import { Client, Storage, ID, Query, Databases } from "appwrite";

import { v4 as uuidv4 } from 'uuid'; // optional for naming the image

// Registration validation schema
const registerSchema = yup.object().shape({
    firstName: yup.string().required("Required"),
    lastName: yup.string().required("Required"),
    username: yup.string().required("Required").min(3, "Username must be at least 3 characters"),
    email: yup.string()
        .test("is-valid-email", "Invalid email", (value) => validator.isEmail(value))
        .required("Required"),
    password: yup.string()
        .required("Required")
        .min(8, "Password must be at least 8 characters")
        .matches(/[A-Z]/, "Password must contain at least one uppercase letter")
        .matches(/[a-z]/, "Password must contain at least one lowercase letter")
        .matches(/[0-9]/, "Password must contain at least one digit")
        .matches(/[!@#$%^&*]/, "Password must contain at least one special character (!@#$%^&*)"),
    dob: yup.date().required("Required"),
    gender: yup.string().required("Required"),
    location: yup.string().required("Required"),
    picture: yup.string().required("Required"),
});

// Initial values for registration form
const initialValuesRegister = {
    firstName: "",
    lastName: "",
    username: "",
    email: "",
    password: "",
    dob: "",
    gender: "",
    location: "",
    picture: "",
};

const SignUp = () => {
    const { palette } = useTheme();
    const navigate = useNavigate();
    const isNonMobile = useMediaQuery("(min-width:1200px)");
    const isMobile = useMediaQuery("(max-width:600px)");
    const [imagePreview, setImagePreview] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "error" });
    const [passwordVisible, setPasswordVisible] = useState(false);
    
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
    const [showCropModal, setShowCropModal] = useState(false);
    const [selectedImage, setSelectedImage] = useState(null);
    
    const [showResendOption, setShowResendOption] = useState(false);
const [userEmail, setUserEmail] = useState("");
const [isResending, setIsResending] = useState(false);
    

    // Cleanup image preview when component unmounts
    useEffect(() => {
        return () => {
            if (imagePreview) {
                URL.revokeObjectURL(imagePreview);
            }
        };
    }, [imagePreview]);

    // Close Snackbar
    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };


const handleResendVerification = async () => {
    if (!userEmail) {
        setSnackbar({
            open: true,
            message: "Email not found. Please try registering again.",
            severity: "error"
        });
        return;
    }

    setIsResending(true);
    try {
        const response = await fetch("https://my-pers-thing-server.onrender.com/auth/resend-verification", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({ email: userEmail }),
        });

        const data = await response.json();

        if (response.ok) {
            setSnackbar({
                open: true,
                message: data.message,
                severity: "success",
            });
        } else {
            setSnackbar({
                open: true,
                message: data.message || "Failed to resend verification email",
                severity: "error",
            });
        }
    } catch (error) {
        setSnackbar({
            open: true,
            message: "Network error. Please try again.",
            severity: "error",
        });
    } finally {
        setIsResending(false);
    }
};

    // Register function to handle form submission
 // Inside SignUp.jsx - Update the register function to handle image upload to Appwrite storage
// Update the register function in SignUp.jsx to show a success message with email verification info
const register = async(values, onSubmitProps) => {
    try {
        // Setup Appwrite client
        const client = new Client();
        client
            .setEndpoint(process.env.REACT_APP_APPWRITE_ENDPOINT)
            .setProject(process.env.REACT_APP_APPWRITE_PROJECT_ID);
        const storage = new Storage(client);
        
        // Upload profile picture to Appwrite storage
        let fileId = null;
        if (values.picture) {
            // Create a unique ID for the file
            const uniqueId = uuidv4();
            
            // Upload file to Appwrite
            const uploadResponse = await storage.createFile(
                process.env.REACT_APP_APPWRITE_BUCKET_ID,
                uniqueId,
                values.picture
            );
            
            fileId = uploadResponse.$id;
        }
        
        // Create FormData to send to backend
        const formData = new FormData();
        for(let value in values){
            // Don't add picture to formData
            if (value !== 'picture') {
                formData.append(value, values[value]);
            }
        }
        
        // Add Appwrite file ID instead of picture path
        formData.append('picturePath', fileId);
        formData.append('appwriteStorage', true); // Flag to indicate using Appwrite storage
        
        const savedUserResponse = await fetch(
            "https://my-pers-thing-server.onrender.com/auth/register",
            {
                method: "POST",
                body: formData,
            }
        );

        if (!savedUserResponse.ok) {
            const errorData = await savedUserResponse.json();
            throw new Error(errorData.msg || "Registration failed");
        }

        const savedUser = await savedUserResponse.json();
        // onSubmitProps.resetForm();

      if (savedUser) {
    // if (imagePreview) {
    //     URL.revokeObjectURL(imagePreview);
    //     setImagePreview(null);
    // }

    // Store email for resend functionality
    setUserEmail(values.email);
    setShowResendOption(true);

    // Show success message about email verification
    setSnackbar({
        open: true,
        message: "Registration complete! Please check your email's inbox/spam for the verification link.",
        severity: "success",
    });

    // Don't redirect to login immediately - wait for verification
    // setTimeout(() => {
    //     navigate("/login");
    // }, 8000); // Redirect after 8 seconds
}
    } catch (err) {
        setSnackbar({ open: true, message: err.message, severity: "error" });
    }
};

    const handleFormSubmit = async(values, onSubmitProps) => {
        await register(values, onSubmitProps);
    };

    // Define a more specific breakpoint to hide the logo when it would shift above the form
    const isLayoutShifting = useMediaQuery("(max-width:1100px)");

return (
    <Container maxWidth="xl" sx={{ 
        minHeight: '100vh', 
        display: 'flex', 
        flexDirection: 'column',
        pt: { xs: 0.5, md: 2 },
        px: { xs: 1, sm: 2, md: 3 },
        overflow: 'hidden',
    }}>
        <style>
            {`
                body {
                    margin: 0;
                    padding: 0;
                    overflow-x: hidden;
                }
            `}
        </style>
        
        <Box sx={{ 
            textAlign: "center", 
            mt: { xs: 1, md: 2 },
            mb: { xs: 1, md: 1 }
        }}>
            <Typography
                variant="h4"
                sx={{
                    fontSize: { xs: "16px", sm: "18px", md: "24px" },
                    color: "#3B3B3B",
                    textTransform: "uppercase",
                    mb: 0.5
                }}
            >
                Join 40+ creators showcasing their talent on BitsBrite — start now!
            </Typography>

            <Typography
                variant="h6"
                sx={{
                    fontSize: { xs: "12px", sm: "14px", md: "18px" },
                    color: "#555555",
                    fontWeight: "normal",
                }}
            >
                Singing 🎤, Rapping 🎶, Dancing 💃, Acting 🎭, Magic ✨, Comedy 😂, Art 🎨, Photography 📸, Instrumental 🎸, and more!
            </Typography>
        </Box>

        {/* Main content container */}
        <Box sx={{ 
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'center',
            alignItems: { xs: 'center', md: 'center' }, // Changed from 'flex-start' to 'center'
            flexGrow: 1,
            width: '100%',
            mt: 2
        }}>
            {/* Logo Section - Hidden when layout would shift */}
            {isNonMobile && !isLayoutShifting && (
                <Box sx={{ 
                    width: { md: '30%' },
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    pr: { md: 4 },
                    // Added these properties to center the logo vertically
                    height: '100%',
                    alignSelf: 'center'
                }}>
                    <Box sx={{ 
                        maxWidth: '200px', 
                        width: '100%',
                        // Optional: add some margin or padding to fine-tune positioning
                        my: 'auto'
                    }}>
                        <img 
                            src={logo}
                            alt="Logo" 
                            style={{ width: '100%', height: 'auto', borderRadius: '10px' }} 
                        />
                    </Box>
                </Box>
            )}

            {/* Form Section */}
            <Box sx={{
                width: { xs: '100%', md: isNonMobile && !isLayoutShifting ? '70%' : '80%' },
                maxWidth: '800px',
                maxHeight: { xs: '100%', md: '80vh' },
                overflowY: 'auto'
            }}>
                <Box 
                    sx={{
                        padding: { xs: '10px', sm: '15px', md: '20px' },
                        border: '1px solid #ddd',
                        borderRadius: { xs: '15px', sm: '20px', md: '30px' },
                        backgroundColor: '#fff',
                        width: '100%',
                        mb: 2
                    }}
                >
                    <Snackbar
                        open={snackbar.open}
                        autoHideDuration={8000}
                        onClose={handleCloseSnackbar}
                        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                    >
                        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{
                            width: "100%",
                            fontSize: "16px",
                            borderRadius: "12px",
                            "& .MuiAlert-icon": {
                                fontSize: "24px",
                            },
                        }}>
                            {snackbar.message}
                        </Alert>
                    </Snackbar>

                    <Formik
                        onSubmit={handleFormSubmit}
                        initialValues={initialValuesRegister}
                        validationSchema={registerSchema}
                    >
                        {({
                            values,
                            errors,
                            touched,
                            handleBlur,
                            handleChange,
                            handleSubmit,
                            setFieldValue,
                            resetForm,
                        }) => (
                            <form onSubmit={handleSubmit}>
                            <Dialog 
                                open={showCropModal} 
                                onClose={() => setShowCropModal(false)} 
                                fullWidth 
                                maxWidth="sm"
                                sx={{
                                    '& .MuiDialog-paper': {
                                        width: '100%',
                                        margin: { xs: '16px', sm: '32px' },
                                        maxHeight: { xs: '90vh', sm: '80vh' }
                                    }
                                }}
                            >
                                <Box sx={{ 
                                    position: 'relative', 
                                    height: { xs: 300, sm: 400 }, 
                                    backgroundColor: '#fff',
                                    width: '100%'
                                }}>
                                    {selectedImage && (
                                        <Cropper
                                            image={selectedImage}
                                            crop={crop}
                                            zoom={zoom}
                                            aspect={1}
                                            onCropChange={setCrop}
                                            onZoomChange={setZoom}
                                            onCropComplete={(_, croppedAreaPixels) => setCroppedAreaPixels(croppedAreaPixels)}
                                        />
                                    )}
                                </Box>
                                <Box 
                                    display="flex" 
                                    flexDirection={{ xs: 'column', sm: 'row' }}
                                    justifyContent="space-between" 
                                    alignItems="center" 
                                    p={2}
                                    gap={2}
                                >
                                    <Typography sx={{ fontSize: { xs: '14px', sm: '16px' } }}>
                                        Zoom:
                                    </Typography>
                                    <Slider
                                        value={zoom}
                                        min={1}
                                        max={3}
                                        step={0.1}
                                        onChange={(e, zoom) => setZoom(zoom)}
                                        sx={{ 
                                            width: { xs: "100%", sm: "60%" },
                                            '& .MuiSlider-thumb': {
                                                height: 20,
                                                width: 20,
                                            },
                                            '& .MuiSlider-track': {
                                                height: 8
                                            },
                                            '& .MuiSlider-rail': {
                                                height: 8
                                            }
                                        }}
                                    />
                                    <Button
                                        variant="contained"
                                        onClick={async () => {
                                            try {
                                                if (selectedImage && croppedAreaPixels) {
                                                    const croppedImage = await getCroppedImg(selectedImage, croppedAreaPixels);
                                                    const blob = await fetch(croppedImage).then(r => r.blob());
                                                    const file = new File([blob], `${uuidv4()}.jpg`, { type: "image/jpeg" });
                                                    setFieldValue("picture", file);
                                                    setImagePreview(croppedImage);
                                                    setShowCropModal(false);
                                                }
                                            } catch (error) {
                                                setSnackbar({
                                                    open: true,
                                                    message: "Failed to crop image",
                                                    severity: "error"
                                                });
                                                setShowCropModal(false);
                                            }
                                        }}
                                        sx={{ 
                                            backgroundColor: "#FF61D2", 
                                            color: "#fff",
                                            width: { xs: '100%', sm: 'auto' },
                                            '&:hover': {
                                                backgroundColor: "#e04dbb",
                                            }
                                        }}
                                    >
                                        Crop & Save
                                    </Button>
                                </Box>
                            </Dialog>
                                
                                <Box
                                    display="grid"
                                    gap={{ xs: "10px", sm: "15px" }}
                                    gridTemplateColumns="repeat(4, minmax(0, 1fr))"
                                    sx={{
                                        "& > div": { gridColumn: isNonMobile ? undefined : "span 4" },
                                        width: '100%'
                                    }}
                                >                       
                                 
                                {/* Profile Picture Upload */}
                                <Box
                                    display="flex"
                                    justifyContent="center"
                                    alignItems="center"
                                    gap="1rem"
                                    sx={{
                                        gridColumn: "span 4",
                                        marginBottom: "0.5rem", 
                                        flexDirection: { xs: 'column', sm: 'row' },
                                        textAlign: 'center'
                                    }}
                                >
                                    <Dropzone
                                        acceptedFiles=".jpg,.jpeg,.png"
                                        multiple={false}
                                        onDrop={(acceptedFiles) => {
                                            if (acceptedFiles && acceptedFiles.length > 0) {
                                                const file = acceptedFiles[0];
                                                // Check file size - limit to 5MB
                                                if (file.size > 5 * 1024 * 1024) {
                                                    setSnackbar({
                                                        open: true,
                                                        message: "Image must be less than 5MB",
                                                        severity: "error"
                                                    });
                                                    return;
                                                }
                                                
                                                const reader = new FileReader();
                                                reader.readAsDataURL(file);
                                                reader.onload = () => {
                                                    setSelectedImage(reader.result); // Base64 string
                                                    setShowCropModal(true); // Show cropping modal
                                                };
                                                reader.onerror = () => {
                                                    setSnackbar({
                                                        open: true,
                                                        message: "Failed to process image",
                                                        severity: "error"
                                                    });
                                                };
                                            }
                                        }}
                                    >
                                        {({ getRootProps, getInputProps, isDragActive }) => (
                                            <Box
                                                {...getRootProps()}
                                                width={{ xs: '100px', sm: '80px' }}
                                                height={{ xs: '100px', sm: '80px' }}
                                                border={`2px dashed ${errors.picture && touched.picture ? 'red' : isDragActive ? "#FF61D2" : "#ccc"}`}
                                                borderRadius="50%"
                                                display="flex"
                                                alignItems="center"
                                                justifyContent="center"
                                                overflow="hidden"
                                                sx={{
                                                    backgroundColor: '#f9f9f9',
                                                    cursor: 'pointer',
                                                    transition: 'all 0.3s ease',
                                                    '&:hover': {
                                                        backgroundColor: '#f0f0f0',
                                                        borderColor: '#FF61D2',
                                                        transform: 'scale(1.05)',
                                                    },
                                                mx: { xs: 'auto', sm: 'unset' }
                                                }}
                                            >
                                                <input {...getInputProps()} />
                                                {imagePreview ? (
                                                    <img
                                                        src={imagePreview}
                                                        alt="Preview"
                                                        style={{
                                                            width: '100%',
                                                            height: '100%',
                                                            objectFit: 'cover',
                                                        }}
                                                    />
                                                ) : (
                                                    <EditOutlinedIcon
                                                        sx={{
                                                            fontSize: { xs: '32px', sm: '28px' },
                                                            color: '#ccc',
                                                        }}
                                                    />
                                                )}
                                            </Box>
                                        )}
                                    </Dropzone>

                                    {/* Label */}
                                    <Box sx={{ mt: { xs: 1, sm: 0 } }}>
                                        <Typography fontSize={{ xs: "16px", sm: "14px" }} fontWeight="500">
                                            Profile Picture<span style={{ color: 'red' }}> *</span>
                                        </Typography>
                                        <Typography fontSize={{ xs: "14px", sm: "12px" }} color="#999">
                                            Click or tap to upload
                                        </Typography>
                                        {touched.picture && errors.picture && (
                                            <Typography fontSize="12px" color="error">
                                                {errors.picture}
                                            </Typography>
                                        )}
                                    </Box>
                                </Box>
                                    {/* First Name */}
                                    <TextField
                                        label={
                                            <>
                                                First Name<span style={{ color: 'red' }}>*</span>
                                            </>
                                        }
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.firstName}
                                        name="firstName"
                                        error={Boolean(touched.firstName) && Boolean(errors.firstName)}
                                        helperText={touched.firstName && errors.firstName}
                                        sx={{ 
                                            gridColumn: { xs: "span 4", sm: "span 2" }, 
                                            "& .MuiOutlinedInput-root": {
                                                borderRadius: "50px", 
                                                '& fieldset': {
                                                    borderColor: '#ccc',
                                                },
                                                '&:hover fieldset': {
                                                    borderColor: '#FF61D2',
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: '#FF61D2',
                                                    borderWidth: '2px',
                                                },
                                            },
                                            "& .MuiInputLabel-root": {
                                                color: '#999',
                                            },
                                            "& .MuiInputLabel-root.Mui-focused": {
                                                color: '#FF61D2',
                                            },
                                        }}
                                    />

                                    {/* Last Name */}
                                    <TextField
                                        label={
                                            <>
                                                Last Name<span style={{ color: 'red' }}>*</span>
                                            </>
                                        }
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.lastName}
                                        name="lastName"
                                        error={Boolean(touched.lastName) && Boolean(errors.lastName)}
                                        helperText={touched.lastName && errors.lastName}
                                        sx={{
                                            gridColumn: { xs: "span 4", sm: "span 2" },
                                            "& .MuiOutlinedInput-root": {
                                                borderRadius: "50px", 
                                                '& fieldset': {
                                                    borderColor: '#ccc',
                                                },
                                                '&:hover fieldset': {
                                                    borderColor: '#FF61D2',
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: '#FF61D2',
                                                    borderWidth: '2px',
                                                },
                                            },
                                            "& .MuiInputLabel-root": {
                                                color: '#999',
                                            },
                                            "& .MuiInputLabel-root.Mui-focused": {
                                                color: '#FF61D2',
                                            },
                                        }}
                                    />

                                    {/* Username */}
                                    <TextField
                                        label={
                                            <>
                                                Username<span style={{ color: 'red' }}>*</span>
                                            </>
                                        }
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.username}
                                        name="username"
                                        error={Boolean(touched.username) && Boolean(errors.username)}
                                        helperText={touched.username && errors.username}
                                        sx={{
                                            gridColumn: { xs: "span 4", sm: "span 2" },
                                            "& .MuiOutlinedInput-root": {
                                                borderRadius: "50px", 
                                                '& fieldset': {
                                                    borderColor: '#ccc',
                                                },
                                                '&:hover fieldset': {
                                                    borderColor: '#FF61D2',
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: '#FF61D2',
                                                    borderWidth: '2px',
                                                },
                                            },
                                            "& .MuiInputLabel-root": {
                                                color: '#999',
                                            },
                                            "& .MuiInputLabel-root.Mui-focused": {
                                                color: '#FF61D2',
                                            },
                                        }}
                                    />

                                    {/* Gender */}
                                    <FormControl
                                        error={Boolean(touched.gender) && Boolean(errors.gender)}
                                        sx={{
                                            gridColumn: { xs: "span 2", sm: "span 1" },
                                            "& .MuiOutlinedInput-root": {
                                                borderRadius: "50px", 
                                                '& fieldset': {
                                                    borderColor: '#ccc',
                                                },
                                                '&:hover fieldset': {
                                                    borderColor: '#FF61D2',
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: '#FF61D2',
                                                    borderWidth: '2px',
                                                },
                                            },
                                            "& .MuiInputLabel-root": {
                                                color: '#999',
                                            },
                                            "& .MuiInputLabel-root.Mui-focused": {
                                                color: '#FF61D2',
                                            },
                                        }}
                                    >
                                        <InputLabel
                                            shrink={!!values.gender}
                                            sx={{
                                                top: values.gender ? '0' : '0px',
                                                transition: 'top 0.2s ease, font-size 0.2s ease',
                                                fontSize: values.gender ? '12px' : '16px',
                                                color: errors.gender ? 'red' : 'rgba(0, 0, 0, 0.54)',
                                            }}
                                        >
                                            Gender<span style={{ color: 'red' }}>*</span>
                                        </InputLabel>
                                        <Select
                                            name="gender"
                                            value={values.gender}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            displayEmpty
                                            sx={{
                                                height: "52px",
                                                '& .MuiSelect-select': {
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                }
                                            }}
                                        >
                                            <MenuItem value="" disabled></MenuItem>
                                            <MenuItem value="Male">Male</MenuItem>
                                            <MenuItem value="Female">Female</MenuItem>
                                            <MenuItem value="Other">Other</MenuItem>
                                        </Select>
                                    </FormControl>

                                    {/* Date of Birth */}
                                    <TextField
                                        label={
                                            <>
                                                Date of Birth<span style={{ color: 'red' }}>*</span>
                                            </>
                                        }
                                        type="date"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.dob}
                                        name="dob"
                                        error={Boolean(touched.dob) && Boolean(errors.dob)}
                                        helperText={touched.dob && errors.dob}
                                        InputLabelProps={{ 
                                            shrink: true,
                                            style: { 
                                                fontSize: '14px',
                                                transform: 'translate(14px, -9px) scale(0.75)'
                                            }
                                        }}
                                        inputProps={{
                                            style: { 
                                                fontSize: { xs: '16px', sm: '14px' },
                                                padding: { xs: '12px 14px', sm: '10px 14px' }
                                            },
                                            // This makes the date picker work better on iOS
                                            min: "1900-01-01",
                                            max: new Date().toISOString().split('T')[0] // Today's date as max
                                        }}
                                        sx={{
                                            gridColumn: { xs: "span 2", sm: "span 1" },
                                            "& .MuiOutlinedInput-root": {
                                                borderRadius: "50px",
                                                height: { xs: '52px', sm: '52px' }, 
                                                '& fieldset': {
                                                    borderColor: '#ccc',
                                                },
                                                '&:hover fieldset': {
                                                    borderColor: '#FF61D2',
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: '#FF61D2',
                                                    borderWidth: '2px',
                                                },
                                                // This helps fix mobile date input sizing
                                                '& input': {
                                                    fontSize: { xs: '16px', sm: '14px' }, // iOS zoom prevention
                                                    padding: { xs: '12px 14px', sm: '10px 14px' }
                                                }
                                            },
                                            "& .MuiInputLabel-root": {
                                                color: '#999',
                                            },
                                            "& .MuiInputLabel-root.Mui-focused": {
                                                color: '#FF61D2',
                                            },
                                            // This helps fix mobile date input dropdown
                                            "& input[type=date]::-webkit-calendar-picker-indicator": {
                                                cursor: 'pointer',
                                                opacity: 0.8,
                                                filter: 'invert(0.5)',
                                                '&:hover': {
                                                    opacity: 1
                                                }
                                            }
                                        }}
                                    />

                                    {/* Location */}
                                    <TextField
                                        label={
                                            <>
                                                Location<span style={{ color: 'red' }}>*</span>
                                            </>
                                        }
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.location}
                                        name="location"
                                        error={Boolean(touched.location) && Boolean(errors.location)}
                                        helperText={touched.location && errors.location}
                                        sx={{
                                            gridColumn: "span 4",
                                            "& .MuiOutlinedInput-root": {
                                                borderRadius: "50px", 
                                                '& fieldset': {
                                                    borderColor: '#ccc',
                                                },
                                                '&:hover fieldset': {
                                                    borderColor: '#FF61D2',
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: '#FF61D2',
                                                    borderWidth: '2px',
                                                },
                                            },
                                            "& .MuiInputLabel-root": {
                                                color: '#999',
                                            },
                                            "& .MuiInputLabel-root.Mui-focused": {
                                                color: '#FF61D2',
                                            },
                                        }}
                                    />

                                    {/* Email */}
                                    <TextField
                                        label={
                                            <>
                                                Email<span style={{ color: 'red' }}>*</span>
                                            </>
                                        }
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.email}
                                        name="email"
                                        error={Boolean(touched.email) && Boolean(errors.email)}
                                        helperText={touched.email && errors.email}
                                        sx={{
                                            gridColumn: "span 4",
                                            "& .MuiOutlinedInput-root": {
                                                borderRadius: "50px", 
                                                '& fieldset': {
                                                    borderColor: '#ccc',
                                                },
                                                '&:hover fieldset': {
                                                    borderColor: '#FF61D2',
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: '#FF61D2',
                                                    borderWidth: '2px',
                                                },
                                            },
                                            "& .MuiInputLabel-root": {
                                                color: '#999',
                                            },
                                            "& .MuiInputLabel-root.Mui-focused": {
                                                color: '#FF61D2',
                                            },
                                        }}
                                    />

                                    {/* Password */}
                                    <TextField
                                        label={
                                            <>
                                                Password<span style={{ color: 'red' }}>*</span>
                                            </>
                                        }
                                        type={passwordVisible ? 'text' : 'password'}
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.password}
                                        name="password"
                                        error={Boolean(touched.password) && Boolean(errors.password)}
                                        helperText={touched.password && errors.password}
                                        InputProps={{
                                            endAdornment: (
                                                <span
                                                    style={{
                                                        cursor: 'pointer',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        marginRight: '10px',
                                                        color: '#999'
                                                    }}
                                                    onClick={() => setPasswordVisible((prev) => !prev)}
                                                >
                                                    {passwordVisible ? <FaEyeSlash /> : <FaEye />}
                                                </span>
                                            ),
                                        }}
                                        sx={{
                                            gridColumn: "span 4",
                                            "& .MuiOutlinedInput-root": {
                                                borderRadius: "50px",
                                                '& fieldset': {
                                                    borderColor: '#ccc',
                                                },
                                                '&:hover fieldset': {
                                                    borderColor: '#FF61D2',
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: '#FF61D2',
                                                    borderWidth: '2px',
                                                },
                                            },
                                            "& .MuiInputLabel-root": {
                                                color: '#999',
                                            },
                                            "& .MuiInputLabel-root.Mui-focused": {
                                                color: '#FF61D2',
                                            },
                                        }}
                                    />
                                </Box>
                                
                                {/* Buttons */}
                                <Box>
                                    <Button
                                        fullWidth
                                        type="submit"
                                        sx={{
                                            m: { xs: "0.8rem 0", sm: "1.2rem 0", md: "1.5rem 0" },
                                            p: { xs: "0.6rem", sm: "0.7rem", md: "0.8rem" },
                                            background: "linear-gradient(135deg, #FF61D2 0%, #FE9090 100%)",
                                            color: "#fff",
                                            borderRadius: "50px",
                                            transition: "all 0.3s ease",
                                            fontSize: { xs: "14px", sm: "16px" },
                                            "&:hover": {
                                                background: "linear-gradient(135deg, #FE9090 0%, #FF61D2 100%)",
                                                transform: "scale(1.05)",
                                            },
                                        }}
                                    >
                                        Sign Up
                                    </Button>

                                    {/* Grey text below Sign Up button */}
<Typography
    sx={{
        textAlign: 'center',
        color: '#888888',
        fontSize: { xs: '13px', sm: '14px', md: '15px' },
        mt: { xs: '0.5rem', sm: '0.8rem', md: '1rem' },
        mb: { xs: '0.5rem', sm: '0.8rem', md: '1rem' },
        fontWeight: '400'
    }}
>
    It only takes a minute — and it's free!
</Typography>

                                    {/* Resend Verification Button - Show only after successful registration */}
{showResendOption && (
    <Button
        fullWidth
        onClick={handleResendVerification}
        disabled={isResending}
        sx={{
            m: { xs: "0.5rem 0", sm: "0.8rem 0", md: "1rem 0" },
            p: { xs: "0.6rem", sm: "0.7rem", md: "0.8rem" },
            background: "linear-gradient(135deg, #4CAF50 0%, #45a049 100%)",
            color: "#fff",
            borderRadius: "50px",
            transition: "all 0.3s ease",
            fontSize: { xs: "14px", sm: "16px" },
            "&:hover": {
                background: "linear-gradient(135deg, #45a049 0%, #4CAF50 100%)",
                transform: "scale(1.05)",
            },
            "&:disabled": {
                background: "#ccc",
                transform: "none",
            },
        }}
    >
        {isResending ? "Resending..." : "Resend Verification Email"}
    </Button>
)}

                                    <Box 
                                        display="flex" 
                                        justifyContent="space-between" 
                                        alignItems="center"
                                    >
                                        <Typography
                                            onClick={() => {
                                                navigate("/login");
                                                resetForm();
                                            }}
                                            sx={{
                                                color: "#555555",
                                                fontSize: { xs: "12px", sm: "14px" },
                                                "&:hover": {
                                                    cursor: "pointer",
                                                    textDecoration: "underline",
                                                },
                                                mt: "10px",
                                            }}
                                        >
                                            Login
                                        </Typography>

                                        <Typography
                                            onClick={() => navigate("/forgot-password")}
                                            sx={{
                                                color: "#555555",
                                                fontSize: { xs: "12px", sm: "14px" },
                                                "&:hover": {
                                                    cursor: "pointer",
                                                    textDecoration: "underline",
                                                },
                                                mt: "10px",
                                            }}
                                        >
                                            Forgot Password?
                                        </Typography>
                                    </Box>
                                </Box>
                            </form>
                        )}
                    </Formik>
                </Box>
            </Box>
        </Box>
    </Container>
);};

export default SignUp;
